/**
 * Responsibility: Implement npc logic cache behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
// ============================================================
// ENGINE/NPC-LOGIC-CACHE.TS — NPC attack function cache
//
// NPC attack functions are keyed by normalized identity string
// (not save-ID), since the same archetype behaves the same across
// saves. Stored per-user so auth rules are satisfied without
// requiring public write access.
//
// Firestore path: artifacts/{appId}/users/{uid}/npc-logic/{key}
//
// Future architecture work is tracked in kanban: only move this cache to a
// shared collection once a validated write path can bound forged-code trust.
// ============================================================

import type { NpcCacheEntry } from "@anyquest/shared-core"
import { loadNpcLogicCache as backendLoadNpcLogicCache, writeNpcLogicCache as backendWriteNpcLogicCache } from "@anyquest/shared-core"
// In-memory store survives the whole browser session
const memCache = new Map<string, NpcCacheEntry>()
let uid = ''
/** Call once when a game session starts (new game or load). */
export function initNpcLogicCache(newUid: string): void {
  uid = newUid
  // Do NOT clear memCache on re-init — NPC functions are global by identity,
  // so cached entries from a previous session load remain valid.
}

export function normalizeNpcKey(identity: string): string {
  // Lowercase, collapse whitespace, truncate — becomes the DB ID
  return identity.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').slice(0, 60)
}

const META_ENV = ((import.meta as ImportMeta & { env?: Record<string, string | boolean | undefined> }).env) ?? {}
const E2E_MOCK_MODE = !META_ENV.PROD && META_ENV.VITE_E2E_MOCK_MODE === '1'

/** Returns cached entry or null. Checks memory first, then Supabase. */
export async function getCachedNpcFn(identity: string): Promise<NpcCacheEntry | null> {
  const key = normalizeNpcKey(identity)
  if (memCache.has(key)) return memCache.get(key)!
  if (!uid || E2E_MOCK_MODE) return null
  try {
    const entry = await backendLoadNpcLogicCache(uid, key) as NpcCacheEntry | null
    if (!entry) return null
    memCache.set(key, entry)
    console.debug(`[NpcLogicCache] Supabase hit: ${identity.slice(0, 30)}`)
    return entry
  } catch (err) {
    console.warn('[NpcLogicCache] Supabase read failed:', err)
  }

  return null
}

/** Store a generated NPC attack function. */
export async function cacheNpcFn(
  identity: string,
  fn: string,
  archetype: string,
): Promise<void> {
  const key = normalizeNpcKey(identity)
  const entry: NpcCacheEntry = { fn, archetype, createdAt: Date.now() }
  memCache.set(key, entry)
  if (!uid || E2E_MOCK_MODE) return
  try {
    await backendWriteNpcLogicCache(uid, key, entry)
    console.debug(`[NpcLogicCache] Persisted NPC fn: ${identity.slice(0, 30)} (${archetype})`)
  } catch (err) {
    console.warn('[NpcLogicCache] Persist failed (in-memory still active):', err)
  }
}
